import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Search,
} from "lucide-react";
import { formatRelativeTime } from "@/lib/date";
import { queryKeys } from "@/lib/queryKeys";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { listArtifacts } from "@/api/artifacts";
import type { HtmlArtifactSummary } from "@/api/artifacts";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableSkeleton } from "@/components/ui/table-skeleton";

const PAGE_SIZE = 50;

interface ArtifactsPageProps {
  projectSlug?: string;
}

export function ArtifactsPage({ projectSlug }: ArtifactsPageProps = {}) {
  const { t } = useTranslation();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const [projectFilter, setProjectFilter] = useState("");
  const debouncedFilter = useDebouncedValue(projectFilter);
  const [offset, setOffset] = useState(0);

  const filterParams: {
    project_id?: string;
    limit: number;
    offset: number;
  } = projectSlug
    ? { limit: PAGE_SIZE, offset, project_id: projectSlug }
    : {
        limit: PAGE_SIZE,
        offset,
        ...(debouncedFilter ? { project_id: debouncedFilter } : {}),
      };

  const {
    data: listData,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.artifacts.list(filterParams),
    queryFn: () => listArtifacts(filterParams),
  });

  const artifacts = listData?.artifacts ?? [];
  const total = listData?.total ?? 0;
  const hasNext = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;
  const showPagination = total > PAGE_SIZE;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileText}
        title={t("artifactsPage.title")}
        subtitle={t("artifactsPage.subtitle")}
        secondaryGlow={false}
        stats={
          !isLoading && total > 0
            ? [{ value: total, label: "Total" }]
            : undefined
        }
      />
      <div className="space-y-4">
        {!projectSlug && (
          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder={t("artifactsPage.filterPlaceholder")}
              value={projectFilter}
              onChange={(e) => {
                setProjectFilter(e.target.value);
                setOffset(0);
              }}
              data-testid="project-filter"
            />
          </div>
        )}
        {error && (
          <p className="text-sm text-destructive">
            {t("artifactsPage.loadError")}
          </p>
        )}
        {!isLoading && total === 0 ? (
          <EmptyState
            icon={FileText}
            title={t("artifactsPage.emptyTitle")}
            description={t("artifactsPage.emptyDescription")}
          />
        ) : (
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t("artifactsPage.columns.title")}</TableHead>
                  {!projectSlug && <TableHead>{t("artifactsPage.columns.project")}</TableHead>}
                  <TableHead>{t("artifactsPage.columns.type")}</TableHead>
                  <TableHead>{t("artifactsPage.columns.creator")}</TableHead>
                  <TableHead>{t("artifactsPage.columns.created")}</TableHead>
                </TableRow>
              </TableHeader>
              {isLoading ? (
                <TableSkeleton columns={projectSlug ? 4 : 5} />
              ) : (
                <TableBody>
                  {artifacts.map((a: HtmlArtifactSummary) => (
                    <TableRow
                      key={a.id}
                      className="cursor-pointer"
                      onClick={() =>
                        projectSlug
                          ? navigate(
                              `/${orgSlug}/projects/${projectSlug}/artifacts/${a.id}`,
                            )
                          : navigate(`/${orgSlug}/artifacts/${a.id}`)
                      }
                      data-testid={`artifact-row-${a.id}`}
                    >
                      <TableCell className="font-medium">{a.title}</TableCell>
                      {!projectSlug && (
                        <TableCell className="text-muted-foreground">
                          {a.project_id ?? "—"}
                        </TableCell>
                      )}
                      <TableCell className="text-muted-foreground">
                        {a.artifact_type}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {a.created_by ?? "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatRelativeTime(a.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {showPagination && (
              <div className="flex items-center justify-between border-t px-4 py-3 text-sm text-muted-foreground">
                <span data-testid="pagination-info">
                  {t("artifactsPage.pagination.range", {
                    from: offset + 1,
                    to: Math.min(offset + PAGE_SIZE, total),
                    total,
                  })}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                    disabled={!hasPrev}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    {t("artifactsPage.pagination.prev")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                    disabled={!hasNext}
                    data-testid="pagination-next"
                  >
                    {t("artifactsPage.pagination.next")}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

